import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCompanyMutation } from "@/hooks/useCompanyMutation";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/AuthProvider";

export type InventoryItem = {
  id: string;
  name: string;
  category: string;
  qty: number;
  min_qty: number;
  max_qty: number;
  unit: string;
  cost_price: number;
  cost_history: { date: string; price: number }[];
};

export type StockRequest = {
  id: string;
  item_id: string;
  item_name: string;
  current_qty: number;
  requested_qty: number;
  unit: string;
  requested_by: string;
  status: string;
  requested_at: string;
};

export function getInflationIndicator(item: InventoryItem) {
  if (!item.cost_history || item.cost_history.length < 2) return null;
  const sorted = [...item.cost_history].sort(
    (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
  );
  const last = sorted[sorted.length - 1].price;
  const prev = sorted[sorted.length - 2].price;

  if (last > prev) {
    const pct = ((last - prev) / prev) * 100;
    return { type: "up", pct: pct.toFixed(1) };
  } else if (last < prev) {
    const pct = ((prev - last) / prev) * 100;
    return { type: "down", pct: pct.toFixed(1) };
  }
  return null;
}

export function useInventoryData(activeCompanyId: string | null) {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  const { data: inventory = [], isLoading: loadingInv } = useQuery({
    queryKey: ["inventory", activeCompanyId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("inventory")
        .select("*")
        .eq("company_id", activeCompanyId)
        .order("name");
      if (error) throw error;
      return data as InventoryItem[];
    },
    enabled: !!activeCompanyId,
  });

  const { data: requests = [], isLoading: loadingReq } = useQuery({
    queryKey: ["stock_requests", activeCompanyId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("stock_requests")
        .select("*")
        .eq("company_id", activeCompanyId)
        .eq("status", "PENDENTE")
        .order("requested_at", { ascending: false });
      if (error) throw error;
      return data as StockRequest[];
    },
    enabled: !!activeCompanyId,
  });

  const saveItem = useCompanyMutation({
    mutationFn: async (vars: any) => {
      const { id, ...rest } = vars;

      let newHistory;
      if (id) {
        const oldItem = inventory.find((i) => i.id === id);
        if (oldItem && Number(oldItem.cost_price) !== Number(rest.cost_price)) {
          newHistory = [
            ...(oldItem.cost_history || []),
            { date: new Date().toISOString(), price: Number(rest.cost_price) },
          ];
        }
      } else {
        newHistory = [
          { date: new Date().toISOString(), price: Number(rest.cost_price) },
        ];
      }
      if (newHistory) rest.cost_history = newHistory;

      if (id) {
        const { error } = await supabase
          .from("inventory")
          .update(rest)
          .eq("id", id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("inventory").insert({
          id: crypto.randomUUID(),
          ...rest,
        });
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["inventory"] });
    },
    onError: (err: any) => {
      alert(`Erro ao salvar insumo: ${err.message}`);
    },
  });

  const resolveRequest = useCompanyMutation({
    mutationFn: async (vars: {
      requestId: string;
      itemId: string;
      status: "APROVADO" | "REJEITADO";
      approvedQty?: number;
      currentQty?: number;
    }) => {
      const { error: reqErr } = await supabase
        .from("stock_requests")
        .update({
          status: vars.status,
          evaluated_by: user?.email,
          evaluated_at: new Date().toISOString(),
        })
        .eq("id", vars.requestId);
      if (reqErr) throw reqErr;

      if (
        vars.status === "APROVADO" &&
        vars.approvedQty &&
        vars.currentQty !== undefined
      ) {
        const { error: invErr } = await supabase
          .from("inventory")
          .update({
            qty: vars.currentQty + vars.approvedQty,
            updated_at: new Date().toISOString(),
          })
          .eq("id", vars.itemId);
        if (invErr) throw invErr;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["stock_requests"] });
      queryClient.invalidateQueries({ queryKey: ["inventory"] });
    },
  });

  return {
    inventory,
    requests,
    loadingInv,
    loadingReq,
    saveItem,
    resolveRequest,
  };
}
